import { useState, useEffect, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/shared/hooks/store';
import { ABSOLUTE_ROUTES, ROUTES } from '@/shared/constants/routes';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { Button } from '@/shared/ui';
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import OnBoardingLayout from '@/modules/onboarding/components/layout';
import DashboardLayout from '@/modules/dashboard/components/layout';

import FooterBg from '@/assets/images/onboarding/footer-flipped.png';
import Pill from '@/assets/icons/thank-you/pill.svg'
import Truck from '@/assets/icons/thank-you/delivery-truck.svg'
import GoogleReview from '@/assets/icons/thank-you/grs.svg'
import Star from '@/assets/icons/thank-you/star.svg'
import Roll from '@/assets/icons/thank-you/roll.svg'
import Test from '@/assets/icons/thank-you/test.svg'
import Thanks from '@/assets/images/onboarding/thank-you-lady.png';
import Phones from '@/assets/images/onboarding/phones.png';
import AskPrescriber from './ask-prescriber';
import NeedSooner from './need-sooner';
// import { hide } from '@intercom/messenger-js-sdk';
import { STYLE_CLASS_COMPONENT_MAP } from '../onboarding/constants';
import { isNumberZeroOrGreater } from '@/shared/helpers/validations';

import PullMeds from '@/assets/images/onboarding/pull-meds.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { hueOptionsList } from '@/shared/constants/hue-options';

interface ReviewProps {
    reviewText: string;
    name: string;
    iconInitial: string;
    classNames?: string;
    iconColor?: string;
}

const Review = ({ reviewText, name, iconInitial, classNames = '', iconColor = 'bg-primary' }: ReviewProps) => {
    return (
        <div className={`${classNames} flex flex-col gap-3 bg-white p-[15px] xl:p-[25px] w-[200px] md:w-[200px] xl:w-[300px]`}>
            <div className='flex flex-row justify-between items-center'>
                <div className='flex flex-row items-center font-atkinsonHyperlegible'>
                    <span className={`${iconColor} w-[26px] h-[26px] rounded-full flex justify-center items-center text-white mr-[10px]`}>
                        {iconInitial}
                    </span>
                    <div className='flex flex-col'>
                        <span className='block font-atkinsonBold text-8'>{name}</span>
                        <span className='block text-8 text-primary-900'>MedBox Customer</span>
                    </div>
                </div>
                <img src={GoogleReview} className='w-[23px]' />
            </div>
            <div className='flex flex-row'>
                {[...Array(5)].map((_, index) => (
                    <img key={index} src={Star} alt={`Image ${index + 1}`} className="" />
                ))}
            </div>
            <span className='block font-atkinsonHyperlegiblei text-15 sm:text-11'>{reviewText}</span>
        </div>
    )
}


const ThankYou = () => {
    const [loading, setLoading] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isAskPrescriberOpen, setIsAskPrescriberOpen] = useState(false);
    const theme = useTheme();
    const isDownMD = useMediaQuery(theme.breakpoints.down('md'));
    const isDownLG = useMediaQuery(theme.breakpoints.down('lg'));
    const isDownSM = useMediaQuery(theme.breakpoints.down('sm'));
    const currentUser = useAppSelector(selectOnboardingUser);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedHueType = currentUser?.userOnboarding?.details?.medboxHueType;
    const selectedHueOption = useMemo(() => hueOptionsList.find(item => item.id === selectedHueType), [selectedHueType]);
    const [svgScale, setSvgScale] = useState<number | null>(window.innerWidth >= 1600 ? window.innerWidth / 1600 : null);
    const { currentStep } = currentUser?.userOnboarding?.details || {};
    const isOnboardingComplete = currentStep === CustomersStepTypeEnum.Done || (isNumberZeroOrGreater(currentUser?.userProfile?.id) && !currentUser?.userOnboarding);
    const isDashboard = currentStep === CustomersStepTypeEnum.Done;

    const takeMeToMedbox = async () => {
        if (isOnboardingComplete) {
            navigate(`/${ROUTES.DASHBOARD.MAIN}`);
            return;
        }

        try {
            setLoading(true);
            if (currentUser?.userOnboarding) {
                const payload = cloneDeep(currentUser.userOnboarding);
                payload.details = { ...payload.details, currentStep: CustomersStepTypeEnum.Done };
                await dispatch(CustomersController.updateMetadata(payload));
                navigate(`/${ROUTES.DASHBOARD.MAIN}`);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleShare = () => setIsShareOpen(prev => !prev);
    const handleToggleAskPrescriber = () => setIsAskPrescriberOpen(prev => !prev);

    useEffect(() => {
        const onResize = () => window.innerWidth >= 1600 ? setSvgScale(window.innerWidth / 1600) : setSvgScale(null);

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, []);


    useEffect(() => {
        let timeoutId: string | number | NodeJS.Timeout | undefined;

        if (!isAskPrescriberOpen) {
            timeoutId = setTimeout(() => {
                window.Intercom?.('hide');
                setIsShareOpen(true);
            }, 5000);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isAskPrescriberOpen]);

    const completeOnboardingAllowed = import.meta.env.VITE_APP_DASHBOARD_PASS_THROUGH_ENABLED === 'true';

    const whatHappensNext = [
        {
            icon: Pill,
            heading: 'Medication transfer now in progress',
            description: 'MedBox has begun working with your doctors and previous pharmacy to gather all your prescriptions and make sure your profile is up-to-date.',
            className: 'rounded-br-3xl rounded-bl-3xl rounded-tr-3xl' // Bottom-right & top-right rounded
        },
        {
            icon: Truck,
            heading: 'MedBox Welcome Kit arrives',
            description: 'Coming in a few days, this shipment will include the MedBox without the medication roll. Some extra surprise goodies will be included inside!',
            className: 'rounded-br-3xl rounded-bl-3xl rounded-tr-3xl' // Bottom-right & top-right rounded
        },
        {
            icon: Roll,
            heading: 'Personalized medication roll delivered',
            description: 'Your personalized medication roll will arrive with all prescriptions sorted by the dose and labeled with the date and time.',
            className: 'rounded-tr-3xl rounded-tl-3xl rounded-br-3xl' // Top-right & bottom-left rounded
        }
    ];


    const inMeantimeOptions = [
        {
            heading: 'Follow up with your previous pharmacy',
            description: <span>To help expedite your prescription transfers, call your previous pharmacy and have them transfer all your active prescriptions on file to MedBox immediately.</span>
        },
        {
            heading: 'Call your doctor’s office',
            description: <span>
                You may ask your doctor's office to&nbsp;
                <a onClick={handleToggleAskPrescriber} className="text-primary font-atkinsonBold underline hover:text-blue-800 cursor-pointer">
                    Send Your Prescriptions Here
                </a>. This will save you time by avoiding the wait for your current pharmacy to process a transfer request.
            </span>
        },
        {
            heading: 'Track your medications & delivery',
            description: <span>While you wait for your medication shipment, you can track your medication and delivery status through your online account at <span className="text-primary font-atkinsonBold underline">
                my.medbox.com
            </span></span>
        },
        {
            heading: 'Add us to your contact list ',
            description: <span className="font-atkinson">
                Copy and save
                <span className="font-atkinsonBold"> hello@medbox.com </span>
                and
                <span className="font-atkinsonBold"> support@medbox.com </span>
                to your contacts so you don’t miss any important communication from us!
            </span>
        }
    ]

    const stepComponentClassPrefix = STYLE_CLASS_COMPONENT_MAP[CustomersStepTypeEnumPaths.THANKYOU];

    const content = (
        <div className="flex flex-auto flex-col justify-center" data-testid="thank-you-page">
            <div className={`w-full bg-[#faf4ef] flex flex-col items-center justify-around ${isDashboard ? `px-5 lg:px-navbarOffsetLg lx:!px-navbarOffsetLx 3xl:!px-navbarOffset3xl` : 'px-[20px]'}`}>
                {currentStep === CustomersStepTypeEnum.Done && (
                    <div className={`mb-[1.9375rem] max-w-[1394px] w-full`}>
                        <Link className="" to={ABSOLUTE_ROUTES.DASHBOARD.HOME.MAIN}>
                            <Button sx={{ fontSize: '1.0625rem', minWidth: 'initial' }} variant="text">
                                &lt;Back
                            </Button>
                        </Link>
                    </div>
                )}

                <div className={`flex flex-row justify-center pb-[300px] ${isDashboard ? 'max-w-[1394px]' : 'px-[10px] max-w-[1394px]'}`}>
                    <div className='relative flex flex-col md:flex-row justify-center '>
                        <div className={`max-w-full md:w-[600px] flex flex-1 flex-col ${isDashboard ? '' : 'justify-center'} items-start gap-3`}>
                            <span className='max-w-[508px] uppercase block text-16 md:text-18 font-atkinson'>exciting news {currentUser?.userProfile?.firstName}</span>
                            <span className='max-w-[350px] block text-27 md:text-44 font-agrandir'>Your MedBox is on its way!</span>
                            <span className='max-w-[508px] block font-atkinson text-18'>Your MedBox Welcome Kit includes what you need to get the most out of your MedBox pharmacy services. Your medication will arrive separately after your welcome kit is delivered.</span>
                        </div>
                        <div className="relative h-full w-full md:w-2/3 max-w-[795px] pb-[200px] pt-[200px] md:py-0">
                            <img
                                className="h-full w-full object-contain object-center h-[400px]"
                                src={selectedHueOption?.thankYouSrc || hueOptionsList[0].thankYouSrc}
                                alt="thank you hue"
                            />
                            <div className='absolute bottom-[45px] sm:bottom-[130px] md:bottom-[20px] md:left-[20px] flex flex-col gap-3'>
                                <Review
                                    reviewText="SO convenient! I'm loving it and my refill came in plenty of time."
                                    name='Barbara'
                                    iconInitial='B'
                                    classNames='rounded-tl-3xl rounded-br-3xl rounded-bl-3xl'
                                    iconColor='bg-[#F6D4C6]'
                                />
                            </div>

                            <div className={`absolute top-[10px] sm:top-[150px] md:top-[10px] flex flex-col gap-3 ${isDashboard ? 'right-0' : '4xl-min:right-[32px] right-[-10px]'}`}>
                                <Review
                                    reviewText="I just want to say, I am so glad I switched over to the med box. It makes it so easy, especially when you have several pills you have to take. "
                                    name='Candy'
                                    iconInitial='C'
                                    classNames='rounded-tl-3xl rounded-br-3xl rounded-tr-3xl'
                                    iconColor='bg-[#02836F]'
                                />
                            </div>
                        </div>
                        {!isDownLG && (
                            <div className='flex absolute bottom-[-430px] left-0 right-0 flex justify-center z-10'>
                                <div className='flex flex-col sm:flex-row bg-white rounded-3xl overflow-hidden max-w-[1394px] h-[396px]'>
                                    <div className='w-[55%]'>
                                        <img src={Thanks} className='object-cover' />
                                    </div>
                                    <div className='p-[50px] gap-2 flex flex-col gap-1 items-center justify-center w-[45%]'>
                                        <span className='block font-agrandir text-30'>Need medications ASAP? We’ve got you</span>
                                        <span className='block font-atkinson text-18'>If you need a refill or new prescription urgently, we’ll take care of it right away. Your MedBox Care Advisor is standing by.</span>
                                        <Button testId="button-thank-you-chat" className='w-full intercom-launcher' fontClassName='font-dmSans text-16 md:text-18' variant="outlined" onClick={() => { }}>
                                            Chat with Us
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isDownLG && (
                            <div className='flex absolute bottom-[-600px] left-0 right-0 flex justify-center z-10'>
                                <div className=' flex flex-col bg-white rounded-3xl overflow-hidden max-w-[400px] '>
                                    <div className='p-[25px] flex flex-col gap-3 items-center justify-center'>
                                        <span className='block font-agrandir text-30'>Need medications ASAP? We’ve got you</span>
                                        <span className='block font-atkinson text-18'>If you need a refill or new prescription urgently, we’ll take care of it right away. Your MedBox Care Advisor is standing by.</span>
                                        <Button testId="button-thank-you-chat" className='w-full intercom-launcher' variant="outlined" onClick={(e) => {
                                            e.preventDefault();
                                        }}>
                                            Chat with Us
                                        </Button>
                                    </div>
                                    <img src={Thanks} className='object-cover' />

                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div>
                <div className={`flex h-[55px] w-full items-end bg-[#FAF4EF]`}>
                    <img src={FooterBg} alt="" className="relative max-h-[55px] w-full" />
                </div>
                <div className='w-full bg-primary-700 flex justify-center'>
                    <div className='flex flex-col items-center pt-[300px] pb-[180px] lg:pt-[115px] md:pb-[150px] px-[20px]'>
                        <span className='font-agrandir text-white block text-30 md:text-44 w-full mb-[20px]'>What happens next? </span>
                        <div className='flex flex-col gap-[25px] md:flex-row justify-between'>
                            {whatHappensNext.map((item, index) => (
                                <div className={`p-[30px] bg-grey-400 ${item.className} max-w-[340px] flex flex-col gap-3 items-start text-white`} key={index}>
                                    <img src={item.icon} className='h-[30px]' />
                                    <span className='block font-atkinsonBold'>{item.heading}</span>
                                    <span>{item.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className='w-full flex justify-center px-[20px]'>
                <div className='flex-1 flex-row justify-center px-[10px] max-w-[1394px]'>
                    <div className='flex flex-col md:flex-row justify-end md:justify-center items-center w-full md:h-[400px]'>
                        <div className='relative md:flex-1'>
                            <div className='relative h-[200px]'>
                                <div className=' absolute bottom-[30px] md:bottom-[-150px] left-0 right-0 flex justify-center z-10'>
                                    <img
                                        className="w-auto h-[300px] md:h-[500px] object-contain"
                                        src={Phones}
                                        alt="phones"
                                    />
                                </div>

                            </div>
                            {isDownMD && (
                                <div className='flex flex-col max-w-[450px] px-[20px] pb-[20px]'>
                                    <span className='block font-agrandir text-30'>Track your medication and delivery status</span>
                                    <span className='block font-atkinsonHyperlegible text-16'>While you wait for your medication shipment, you can
                                        track your medication and delivery status through your
                                        MedBox Dashboard.</span>
                                    {completeOnboardingAllowed && <Button testId="button-navigate-to-account" loading={loading} className='mt-[20px] thank_you__take_me_to_my_medbox_btn' showEndIcon onClick={takeMeToMedbox}>
                                        Go to my Account
                                    </Button>}
                                </div>
                            )}
                        </div>

                        {!isDownMD && (
                            <div className='flex flex-1 p-[30px]'>
                                <div className='flex-col'>
                                    <span className='block font-agrandir text-36'>Track your medication and delivery status</span>
                                    <span className='block font-atkinsonHyperlegible text-16'>While you wait for your medication shipment, you can
                                        track your medication and delivery status through your
                                        MedBox Dashboard.</span>
                                    {completeOnboardingAllowed && <Button testId="button-navigate-to-account" loading={loading} className='w-full mt-[20px] thank_you__take_me_to_my_medbox_btn' showEndIcon onClick={takeMeToMedbox}>
                                        Go to my Account
                                    </Button>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className='relative flex flex-col items-center justify-around bg-[#EBF3F0] rounded-tr-3xl z-15 py-[50px] overflow-hidden'>
                <img
                    src={Test}
                    alt="Background"
                    style={svgScale ? { objectFit: 'contain', transform: `scale(${svgScale})` } : {}}
                    className="hidden xs:flex absolute bottom-[-500px] w-full h-full object-cover z-[-1]"
                />
                <div className='max-w-[1394px] px-[40px]'>
                    <div className='w-full font-agrandir text-30 md:text-44 mb-[20px]'>
                        <span>What you can do in the meantime...</span>
                    </div>
                    <div className='flex flex-col items-center lg:px-[100px] gap-[34px]'>
                        {inMeantimeOptions.map((item, index) => (
                            <div className='flex flex-row items-center' key={index}>
                                <div className='flex-1 border-1 border-primary-700 p-[30px] rounded-xl'>
                                    <div className='relative flex flex-row items-center'>
                                        <div className='bg-primary-700 absolute left-[-49px] h-[30px] w-[36px] items-center flex justify-center text-white rounded-[15px]'>
                                            {index + 1}
                                        </div>

                                        <div className='flex flex-col gap-3'>
                                            <span className='block font-atkinsonBold text-22'>
                                                {item.heading}
                                            </span>
                                            <span className='block text-20'>
                                                {item.description}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>

            </div>

            <div className='bg-peach-200 w-full flex justify-center'>
                <div className='flex-1 flex-col justify-center max-w-[1394px] px-[20px]'>
                    <div className="w-full flex flex-col sm:flex-row items-center sm:justify-between pt-[40px]">
                        <div className="sm:w-1/2">
                            <span className="font-agrandir text-30 sm:text-44 w-full block">
                                Managing your medications just got a lot easier
                            </span>
                            <span className="font-agrandir text-18 w-full block sm:w-2/3">
                                Congrats on joining the thousands who’ve ditched pill organizers for good!
                            </span>
                        </div>
                        <div className="sm:w-1/2 hidden sm:block" />
                        {isDownSM && (
                            <div className='flex flex-col gap-[10px] mt-[10px]'>
                                <div className='rounded-3xl overflow-hidden'>
                                    <img src={PullMeds} className="w-full object-contain object-center" alt="pull-meds-img" />
                                </div>
                                <Review
                                    reviewText='I want to say there is no place like this Pharmacy they go above and beyond they have became like my blood family for me to talk about them bring tears to my eyes 😥 this Pharmacy goes Way Beyond their job to help out thier patience the staff is amazing there is just no competition for them because to me they are top-notch with a billion stars and Beyond I thank you guys for what you can do thank you thank you thank you again'
                                    name='Veronica'
                                    iconInitial='J'
                                    classNames='rounded-tr-3xl rounded-br-3xl rounded-bl-3xl w-full'
                                    iconColor='bg-[#003882]'
                                />
                                <Review
                                    reviewText='Medbox make taking all my medicine which I take three times a day a breeze I love the service they are quick to answer my questions.'
                                    name='Phillip'
                                    iconInitial='J'
                                    classNames='rounded-tr-3xl rounded-br-3xl rounded-bl-3xl w-full'
                                    iconColor='bg-[#D2C8B7]'
                                />
                                <Review
                                    reviewText='Love love love my medbox from  Ameripharma.  It’s easy to use I no longer need to separate my meds they do it for me.  I never have to wait for them to ask my doctors for a refill they handle and them let me know it’s handled.  Changing from a brick and mortar pharmacy to them was the best decision EVER!'
                                    name='Whitney'
                                    iconInitial='J'
                                    classNames='rounded-tr-3xl rounded-br-3xl rounded-bl-3xl w-full'
                                    iconColor='bg-[#27C6B0]'
                                />
                            </div>
                        )}
                    </div>

                    {!isDownSM && (
                        <div className='w-full flex flex-row items-center justify-between pt-[110px]'>
                            <div className='relative w-1/3 md:w-1/2'>
                                <div className='absolute top-0 right-[-220px]  md:top-[-40px] md:right-[-115px] xl:top-[-95px] flex flex-col gap-3'>
                                    <Review
                                        reviewText='Medbox make taking all my medicine which I take three times a day a breeze I love the service they are quick to answer my questions.'
                                        name='Phillip'
                                        iconInitial='J'
                                        classNames='rounded-tr-3xl rounded-br-3xl rounded-bl-3xl'
                                        iconColor='bg-[#D2C8B7]'
                                    />
                                </div>
                                <div className='absolute top-[-355px] right-[-120px] md:top-[-400px] md:right-[-115px] flex flex-col gap-3'>
                                    <Review
                                        reviewText='I want to say there is no place like this Pharmacy they go above and beyond they have became like my blood family for me to talk about them bring tears to my eyes 😥 this Pharmacy goes Way Beyond their job to help out thier patience the staff is amazing there is just no competition for them because to me they are top-notch with a billion stars and Beyond I thank you guys for what you can do thank you thank you thank you again'
                                        name='Veronica'
                                        iconInitial='J'
                                        classNames='rounded-tl-3xl rounded-br-3xl rounded-tr-3xl'
                                        iconColor='bg-[#003882]'
                                    />
                                </div>
                                <div className='absolute right-[-5px] top-0 md:top-[-125px] md:right-[105px] xl:right-[205px] xl:top-[-220px] flex flex-col gap-3'>
                                    <Review
                                        reviewText='Love love love my medbox from  Ameripharma.  It’s easy to use I no longer need to separate my meds they do it for me.  I never have to wait for them to ask my doctors for a refill they handle and them let me know it’s handled.  Changing from a brick and mortar pharmacy to them was the best decision EVER!'
                                        name='Whitney'
                                        iconInitial='J'
                                        classNames='rounded-tl-3xl rounded-bl-3xl rounded-br-3xl'
                                        iconColor='bg-[#27C6B0]'
                                    />
                                </div>
                            </div>
                            <div className='w-2/3 md:w-1/2 h-[500px] pb-[50px]'>
                                <div className='rounded-3xl overflow-hidden'>
                                    <img src={PullMeds} className="w-full object-contain object-center" alt="pull-meds-img" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <NeedSooner open={isShareOpen} onClose={handleToggleShare} closeOnClickOutside={false} />
            <AskPrescriber open={isAskPrescriberOpen} onClose={handleToggleAskPrescriber} closeOnClickOutside={false} />
            {/* <ShareModal open={isShareOpen} onClose={handleToggleShare} closeOnClickOutside={false} /> */}
        </div>
    );

    if (isDashboard) {
        return (
            <DashboardLayout hasFooter isThankYou>
                {content}
            </DashboardLayout>
        );
    }

    return (
        <OnBoardingLayout
            hasFooter
            paddings={false}
            stepComponentClassPrefix={stepComponentClassPrefix}
            step='thank-you'
            onboardingPassed
        >
            {content}
        </OnBoardingLayout>
    );
};

export default withScrollRestoration(ThankYou);
